import { useReducer } from 'react';

import isUndefinedOrNull from '@zenbusiness/application-commons-utils/isUndefinedOrNull';
import PropTypes from 'prop-types';

import FormStepModal from '#client/components/FormStepModal';
import { FUND_BANK_ACCOUNT } from '#client/constants/nextUpCards';
import {
  CONFIRMATION_FORM_STEP,
  CHECK_CARD_ERRORS_STEP,
  HOW_MUCH_STEP,
  WHICH_ACCOUNT_STEP
} from '#client/constants/transfer';
import useCheckTransactionContext from '#client/hooks/useCheckTransactionContext';
import { FormStepProvider } from '#client/hooks/useFormStep';
import useInitHotJar from '#client/hooks/useHotJar';
import useModalContext from '#client/hooks/useModalContext';
import useNextUpCardsContext from '#client/hooks/useNextUpCardsContext';
import { dispatchRefreshDataAfterInstantDeposit } from '#client/utils/event';
import { isCurrentPageEqualToTargetPage } from '#client/utils/isCurrentPageEqualToTargetPage';

import CurrentStep from './components/CurrentStep';

import {
  ACCOUNT_COULD_NOT_SET_UP,
  DASHBOARD_NOT_FUND_PAGE,
  INSTANT_DEPOSIT_STEP_LIST,
  LOADING_STEP,
  MODAL_ACTION,
  SETTING_UP_ACCOUNT,
  TRY_ADD_CARD_AGAIN_STEP
} from './constants/instants';
import { depositReducer, initState } from './hooks/depositReducer';

export default function InstantDepositModal({
  steps = INSTANT_DEPOSIT_STEP_LIST,
  handleClose,
  instantFundingAccount,
  actionsKit,
  initStep,
  checkCardErrorsStep,
  createAstraCardError
}) {
  const { removeCardWithAnimation } = useNextUpCardsContext();
  // Initialize HotJar to show surveys after instant deposit
  useInitHotJar();
  const initialFormValue = {
    initStep,
    createAstraCardError
  };

  // Validate card before going to the HOW_MUCH_STEP
  if (checkCardErrorsStep && !initStep) {
    initialFormValue.initStep = CHECK_CARD_ERRORS_STEP;
  }

  if (instantFundingAccount && instantFundingAccount?.uuid) {
    initialFormValue.selectedFromAccount = {
      ...initialFormValue,
      uuid: instantFundingAccount.uuid
    };
  }
  const [depositData, dispatchDepositData] = useReducer(
    depositReducer,
    initialFormValue,
    initState
  );
  const { clearAllModal } = useModalContext();
  const { refetchForFirstTransaction, refetchForDebitCardPayment } = useCheckTransactionContext();

  const { currentFormStep } = depositData;

  const resetSearchParameters = () => {
    if (typeof actionsKit?.resetSearchParameters === 'function') {
      actionsKit.resetSearchParameters();
    }
  };

  const handleBack = () => {
    const currentStepIndex = steps.indexOf(currentFormStep);
    const isClose =
      currentStepIndex <= 0 ||
      currentFormStep === WHICH_ACCOUNT_STEP ||
      (currentFormStep === HOW_MUCH_STEP && instantFundingAccount);

    if (isClose) {
      handleClose();
      resetSearchParameters();
      return;
    }

    dispatchDepositData({ type: MODAL_ACTION.BACK });
  };

  const handleCloseModal = () => {
    handleClose();
    clearAllModal();
    resetSearchParameters();

    /**
     * Checks if the current form step is the confirmation step and the current page is not 'dashboard-notfunded'.
     * If both conditions are met, it dispatches an action to refresh data after an instant deposit.
     */
    if (
      currentFormStep === CONFIRMATION_FORM_STEP &&
      !isCurrentPageEqualToTargetPage(DASHBOARD_NOT_FUND_PAGE)
    ) {
      dispatchRefreshDataAfterInstantDeposit();
      removeCardWithAnimation(FUND_BANK_ACCOUNT);
    }

    /**
     * If instant deposit successfully,
     * we will call the refetchForFirstTransaction and refetchForDebitCardPayment to check the first transaction and astra payment
     */
    if (
      currentFormStep === CONFIRMATION_FORM_STEP &&
      isUndefinedOrNull(depositData?.createTransferError) &&
      isUndefinedOrNull(depositData?.reason)
    ) {
      refetchForFirstTransaction();
      refetchForDebitCardPayment();
    }
  };

  const disableBack = [
    CONFIRMATION_FORM_STEP,
    TRY_ADD_CARD_AGAIN_STEP,
    SETTING_UP_ACCOUNT,
    ACCOUNT_COULD_NOT_SET_UP,
    CHECK_CARD_ERRORS_STEP
  ].includes(currentFormStep);
  const titleBackground = [TRY_ADD_CARD_AGAIN_STEP].includes(currentFormStep)
    ? 'grey.main'
    : 'grey.light';
  const disableCloseModal = [LOADING_STEP, SETTING_UP_ACCOUNT].includes(currentFormStep);

  return (
    <FormStepModal
      disableBack={disableBack}
      disableCloseModal={disableCloseModal}
      handleBack={handleBack}
      handleClose={handleCloseModal}
      hideTitle={disableCloseModal}
      title="Instant deposit"
      titleBackground={titleBackground}
    >
      <FormStepProvider
        value={{
          depositData,
          dispatchDepositData
        }}
      >
        <CurrentStep
          actionsKit={actionsKit}
          closeHandler={handleCloseModal}
          currentFormStep={currentFormStep}
          handleBack={handleBack}
        />
      </FormStepProvider>
    </FormStepModal>
  );
}

InstantDepositModal.propTypes = {
  steps: PropTypes.arrayOf(PropTypes.string),
  initStep: PropTypes.string,
  checkCardErrorsStep: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  actionsKit: PropTypes.shape({
    resetSearchParameters: PropTypes.func
  }),
  instantFundingAccount: PropTypes.shape({
    uuid: PropTypes.string
  }),
  createAstraCardError: PropTypes.string
};
